<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

const FilePath = '/version';
const PollInterval = 30e3;

const visible = ref(false);
const hash = ref(null);
let timeout = null;

function onClickReload() {
  window.location.reload();
}

async function checkHash() {
  try {
    const resp = await fetch(FilePath, { cache: 'no-store' });
    const data = await resp.text();

    if (hash.value === null) {
      hash.value = data;
    } else if (hash.value !== data) {
      hash.value = data;
      visible.value = true;
      return;
    }
  } catch (err) { /* noop */ }

  timeout = setTimeout(() => checkHash(), PollInterval);
}

onMounted(() => {
  checkHash();
});

onBeforeUnmount(() => {
  clearTimeout(timeout);
});
</script>

<template>
  <Transition name="slide-fade">
    <div
      v-if="visible"
      v-bind="$attrs"
      class="version-check-toast"
      data-entity="app-versionReload"
    >
      <div class="version-check-toast__icon">
        <i class="fa-regular fa-circle-exclamation" />
      </div>

      <div class="version-check-toast__body">
        <p class="version-check-toast__title">
          A new version of Tagboard creator is available
        </p>

        <p class="version-check-toast__message">
          Please reload to get the latest changes
        </p>

        <button
          type="button"
          class="version-check-toast__button"
          data-test="version-check-toast"
          @click="onClickReload"
        >
          Reload
        </button>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.version-check-toast {
  position: fixed;
  bottom: calc(0.5rem + 40px);
  right: 1rem;
  z-index: 1000;
  display: flex;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  box-shadow: 0 0.9rem 2.4rem rgba(15, 23, 42, 0.12);
  border-left: 4px solid var(--primary-400);
  max-width: 420px;

  .dark-theme & {
    box-shadow: 0 0.9rem 2.4rem rgba(0, 0, 0, 0.3);
  }
}

.version-check-toast__icon {
  flex-shrink: 0;
  color: var(--primary-400);
  font-size: 1.5rem;
  line-height: 1;
  padding-top: 0.125rem;
}

.version-check-toast__body {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.version-check-toast__title {
  margin: 0;
  font-weight: 700;
  font-size: 1rem;
  color: var(--text-color);
}

.version-check-toast__message {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.version-check-toast__button {
  align-self: flex-start;
  margin-top: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  background: var(--primary-color);
  color: var(--primary-color-text);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-400);
    outline-offset: 2px;
  }
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-enter-from {
  transform: translateY(20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
